import { ReactNode, useEffect, useRef, useState } from 'react'
import { useCurrentBreakpoint } from '../../hooks/useCurrentBreakPoint'
import { CarouselHeightsSizes, SLIDE_SIZES } from '../../models/types/Carousel.type'
import CarouselContainer from './CarouselContainer'
import ScrollArea from './ScrollArea'

const getPageSize = (currentBreakpoint: string, carouselType: CarouselHeightsSizes) => {
  if (['medium', 'average', 'large', 'xl'].includes(currentBreakpoint)) {
    return carouselType === 'small' ? 6 : 3
  }
  if (['tablet'].includes(currentBreakpoint)) {
    return carouselType === 'small' ? 3 : 2
  }
  return carouselType === 'small' ? 2 : 1
}

type CarouselProps = {
    slides: ReactNode[]
    size: CarouselHeightsSizes
    headingId: string
}

export const Carousel = ({ slides, size, headingId }: CarouselProps) => {
  const currentBreakpoint = useCurrentBreakpoint()
  const isLarge = size === 'large'

  const [showButtons, setShowButtons] = useState({
    showLeft: false,
    showRight: false
  })

  const page = getPageSize(currentBreakpoint, size)
  const [centered, setCentered] = useState(slides.length <= page && !isLarge)
  const [activeSlide, setActiveSlide] = useState(0)
  const largeSlideBreakpoint = page === 3 ? 'lg' : page === 2 ? 'md' : 'sm'
  const slideWidthPercentage = parseFloat(SLIDE_SIZES[size][largeSlideBreakpoint]) / 100

  useEffect(() => {
    const newCenter = slides.length <= page && !isLarge
    setCentered(newCenter)

    setShowButtons({
      showLeft: showButtons.showLeft,
      showRight: !newCenter && slides.length > 1
    })
  }, [page])

  const scrollAreaRef = useRef<HTMLUListElement>(null)

  const handleClick = (dir: string) => {
    if (!scrollAreaRef.current) return
    const container = scrollAreaRef.current
    const bound = container.getBoundingClientRect()
    const displacement = isLarge ? bound.width * slideWidthPercentage : bound.width

    container.scrollLeft += dir === 'left' ? -displacement : displacement
  }

  const handleScroll = () => {
    if (!scrollAreaRef.current) return
    const container = scrollAreaRef.current
    const isElementFullyScrolledRight =
      Math.abs(container.scrollWidth - container.scrollLeft - container.clientWidth) <= 1

    const isElementFullyScrolledLeft = container.scrollLeft === 0

    if (isLarge) {
      const scrollPoints = container.clientWidth * slideWidthPercentage - 16
      const currentSlide = container.scrollLeft / Math.floor(scrollPoints)

      if (Math.abs(activeSlide - currentSlide) > 0.5) {
        setActiveSlide(currentSlide > activeSlide ? activeSlide + 1 : activeSlide - 1)
      }
    }

    if (
      showButtons.showLeft &&
      showButtons.showRight &&
      !isElementFullyScrolledLeft &&
      !isElementFullyScrolledRight
    )
      return

    setShowButtons({
      showLeft: !isElementFullyScrolledLeft,
      showRight: !isElementFullyScrolledRight
    })
  }

  return (
    <CarouselContainer
      headingId={headingId}
      size={size}
      isLarge={isLarge}
      handleNavigate={handleClick}
      showButtons={showButtons}>
      <ScrollArea
        size={size}
        isLarge={isLarge}
        centered={centered}
        scrollAreaRef={scrollAreaRef}
        handleScroll={handleScroll}
        handleNavigate={handleClick}
        activeSlide={activeSlide}>
        {slides}
      </ScrollArea>
    </CarouselContainer>
  )
}
